// src/components/BazaarView.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { useRoutePortalTarget } from './layout/RoutePortalHost';
import { getBazaarInboxSnapshot, subscribeBazaarInboxStore, startBazaarInboxStore } from '../services/bazaarInboxStore';
import { getRoddoTheme, getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import { forwardGeocodeServer } from '../services/geocodeClient';
import { buildBazaarListingShareUrl, share } from '../utils/share';
import ConfirmationModal from './ConfirmationModal';
import BazaarDMView from './BazaarDMView';
import BazaarListingDetailView from './BazaarListingDetailView';
import type { MarketplaceItem, ToastType, UserProfile } from '../types';
import {
  MARKETPLACE_CATEGORIES,
  MARKETPLACE_CONDITIONS,
  MARKETPLACE_CURRENCIES,
  archiveMarketplaceItem,
  sellerDeleteMarketplaceItem,
  sellerSetMarketplaceItemPending,
  sellerMakeMarketplaceItemAvailable,
  sellerMarkMarketplaceItemSold,
  subscribeMarketplaceItemsForSeller,
  getMarketplaceSellerHistoryCount,
  getMarketplaceSellerHistoryPage,
  type MarketplaceSellerHistoryCursor,
  createMarketplaceItem,
  reportMarketplaceItem,
  updateMarketplaceItem,
} from '../services/marketplace';
import {
  deleteMarketplaceImages,
  uploadMarketplaceImage,
} from '../services/storage';
import {
  getBazaarDMThreadId,
  subscribeBazaarThreadsForListing,
  refreshMyBazaarThreadLifecycle,
  type BazaarDMThread,
  type BazaarDMSellerThread,
  type BazaarDMBuyerThread,
} from '../services/bazaarDMs';

type BazaarViewProps = {
  marketplaceItems: Record<string, any> | Record<number, any>;
  currentUserProfile?: UserProfile;
  addToast?: (message: string, type?: ToastType) => void;
  isDarkMode?: boolean;
  initialItemId?: string;
  initialThreadId?: string;
  currentCoords?: { lat: number; lon?: number; lng?: number } | null;
};

type Mode = 'all' | 'mine';
type MyListingTab = 'live' | 'history';
type EditState = { open: boolean; docKey: string; item: any | null };
type ReportState = { open: boolean; docKey: string; item: any | null };
type EditPhotoSlot =
  | { id: string; kind: 'existing'; url: string; path?: string }
  | { id: string; kind: 'new'; url: string; file: File };

const safeStr = (value: any) => (typeof value === 'string' ? value : value == null ? '' : String(value));
const MAX_LISTING_IMAGES = 4;

const marketplaceLocationKey = (item: any) =>
  [item?.city, item?.region, item?.countryCode]
    .map((value) => safeStr(value).trim().toLowerCase())
    .filter(Boolean)
    .join('|');

const marketplaceLocationQuery = (item: any) =>
  [item?.city, item?.region, item?.countryCode]
    .map((value) => safeStr(value).trim())
    .filter(Boolean)
    .join(', ');

const distanceKm = (aLat: number, aLng: number, bLat: number, bLng: number) => {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(Math.max(0, 1 - h)));
};

const CATEGORY_LABELS: Record<string, string> = {
  rods: 'Rods',
  reels: 'Reels',
  tackle: 'Tackle',
  electronics: 'Electronics',
  boats_kayaks: 'Boats / Kayaks',
  apparel: 'Apparel',
  storage: 'Storage',
  other: 'Other',
};

const CONDITION_LABELS: Record<string, string> = {
  new: 'New',
  like_new: 'Like new',
  good: 'Good',
  fair: 'Fair',
  parts: 'Parts / repair',
};

function getItemDocKey(item: any, fallback: string) {
  return (
    safeStr(item?.docId).trim() ||
    safeStr(item?.docKey).trim() ||
    safeStr(item?.firestoreId).trim() ||
    safeStr(item?.legacyDocId).trim() ||
    fallback
  );
}

function pickImages(item: any): string[] {
  const values = Array.isArray(item?.imageUrls)
    ? item.imageUrls
    : Array.isArray(item?.images)
      ? item.images
      : [];
  const cleaned = values.map((value: any) => safeStr(value).trim()).filter(Boolean);
  if (cleaned.length) return cleaned;
  const legacy = safeStr(item?.imageUrl || item?.image || item?.photoUrl).trim();
  return legacy ? [legacy] : [];
}

function formatMoney(priceCents: any, currency: any) {
  const cents = Math.max(0, Math.round(Number(priceCents || 0)));
  const code = safeStr(currency || 'usd').trim().toUpperCase() || 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${code} ${(cents / 100).toFixed(2)}`;
  }
}

function formatMessageStamp(ms?: number) {
  const value = Number(ms || 0);
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

function priceInputToCents(value: string) {
  const normalized = value.trim().replace(/,/g, '').replace(/[^0-9.]/g, '');
  const amount = Number(normalized);
  return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : NaN;
}

function buildLocationLabel(city: string, region: string, countryCode: string) {
  return [city.trim(), region.trim(), countryCode.trim().toUpperCase()].filter(Boolean).join(', ');
}

function listingIdNow() {
  return String(Date.now());
}

function fulfillmentLabel(item: any) {
  const methods: string[] = Array.isArray(item?.fulfillmentMethods) ? item.fulfillmentMethods : ['LOCAL_PICKUP'];
  const labels: string[] = [];
  if (methods.includes('LOCAL_PICKUP')) labels.push('Local pickup');
  if (methods.includes('SHIPPING')) labels.push('Shipping available');
  return labels.join(' · ') || 'Contact seller';
}

function listingDetailSyncKey(item: any): string {
  // Only fields rendered by the detail surface / its actions. Firestore snapshots can
  // hand us fresh object identities with unchanged data; comparing this stable key
  // prevents a no-op setState render loop while still reflecting real listing edits.
  try {
    return JSON.stringify({
      title: safeStr(item?.title || item?.name),
      description: safeStr(item?.description),
      priceCents: Number(item?.priceCents || 0),
      currency: safeStr(item?.currency || 'usd'),
      category: safeStr(item?.category),
      condition: safeStr(item?.condition),
      quantity: Number(item?.quantity ?? 1),
      status: safeStr(item?.status || 'active'),
      isSold: Boolean(item?.isSold),
      sellerId: safeStr(item?.sellerId),
      sellerName: safeStr(item?.sellerName),
      sellerUsername: safeStr(item?.sellerUsername),
      sellerAvatar: safeStr(item?.sellerAvatar),
      city: safeStr(item?.city),
      region: safeStr(item?.region),
      countryCode: safeStr(item?.countryCode),
      locationLabel: safeStr(item?.locationLabel),
      fulfillmentMethods: Array.isArray(item?.fulfillmentMethods) ? item.fulfillmentMethods.map(safeStr) : [],
      imageUrls: pickImages(item),
      imagePaths: Array.isArray(item?.imagePaths) ? item.imagePaths.map(safeStr) : [],
    });
  } catch {
    return '';
  }
}

export default function BazaarView({
  marketplaceItems,
  currentUserProfile,
  addToast,
  isDarkMode: isDarkModeProp,
  initialItemId,
  initialThreadId,
  currentCoords,
}: BazaarViewProps) {
  const portalTarget = useRoutePortalTarget();
  const isDarkMode = isDarkModeProp ?? resolveRoddoDarkMode();
  const theme = getRoddoTheme(!!isDarkMode);
  const ui = getRoddoUiTokens(!!isDarkMode);
  const uid = safeStr((currentUserProfile as any)?.uid).trim();
  const displayName =
    safeStr((currentUserProfile as any)?.username).trim() ||
    safeStr((currentUserProfile as any)?.displayName).trim() ||
    'Angler';
  const avatar =
    safeStr((currentUserProfile as any)?.avatar).trim() ||
    safeStr((currentUserProfile as any)?.photoURL).trim() ||
    '';

  const [mode, setMode] = useState<Mode>('all');
  const [myListingTab, setMyListingTab] = useState<MyListingTab>('live');
  const [sellerListings, setSellerListings] = useState<Record<string, any>>({});
  const [sellerHistory, setSellerHistory] = useState<Record<string, any>>({});
  const [sellerHistoryCursor, setSellerHistoryCursor] = useState<MarketplaceSellerHistoryCursor | null>(null);
  const [sellerHistoryHasMore, setSellerHistoryHasMore] = useState(false);
  const [sellerHistoryLoading, setSellerHistoryLoading] = useState(false);
  const [sellerHistoryLoaded, setSellerHistoryLoaded] = useState(false);
  const [sellerHistoryCount, setSellerHistoryCount] = useState(0);
  const sellerHistoryRequestRef = useRef(0);
  const [search, setSearch] = useState('');
  const [distanceByLocationKey, setDistanceByLocationKey] = useState<Record<string, number>>({});
  const proximityRefreshRef = useRef(0);
  const [selectedListing, setSelectedListing] = useState<{ docKey: string; item: any } | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState('');
  const [listingMenuKey, setListingMenuKey] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('usd');
  const [category, setCategory] = useState('tackle');
  const [condition, setCondition] = useState('good');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [localPickup, setLocalPickup] = useState(true);
  const [shipping, setShipping] = useState(false);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const [edit, setEdit] = useState<EditState>({ open: false, docKey: '', item: null });
  const [editSaving, setEditSaving] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editCurrency, setEditCurrency] = useState('usd');
  const [editCategory, setEditCategory] = useState('other');
  const [editCondition, setEditCondition] = useState('good');
  const [editCity, setEditCity] = useState('');
  const [editRegion, setEditRegion] = useState('');
  const [editCountryCode, setEditCountryCode] = useState('US');
  const [editLocalPickup, setEditLocalPickup] = useState(true);
  const [editShipping, setEditShipping] = useState(false);
  const [editPhotoOrder, setEditPhotoOrder] = useState<EditPhotoSlot[]>([]);
  const [editPhotoDragId, setEditPhotoDragId] = useState('');
  const editPhotoDragIdRef = useRef('');
  const editFileRef = useRef<HTMLInputElement>(null);
  const [deletingListing, setDeletingListing] = useState(false);

  const [conversation, setConversation] = useState<{
    open: boolean;
    threadId: string;
    listingId: string;
    item: any | null;
    buyerId: string;
    buyerName: string;
    buyerAvatar?: string;
    startWithOffer?: boolean;
    returnTo?: 'messageCenter' | 'sellerMessages' | 'listing';
  }>({ open: false, threadId: '', listingId: '', item: null, buyerId: '', buyerName: '', returnTo: 'listing' });

  const deepLinkHandledRef = useRef('');

  const [sellerMessagesOpen, setSellerMessagesOpen] = useState<{ open: boolean; docKey: string; item: any | null }>({ open: false, docKey: '', item: null });
  const [sellerThreads, setSellerThreads] = useState<BazaarDMThread[]>([]);
  const [sellerThreadsLoading, setSellerThreadsLoading] = useState(false);
  const [bazaarInboxSnapshot, setBazaarInboxSnapshot] = useState(() =>
    getBazaarInboxSnapshot(uid),
  );
  const sellerInboxThreads = bazaarInboxSnapshot.sellerThreads;
  const buyerInboxThreads = bazaarInboxSnapshot.buyerThreads;
  const sellerInboxReady = bazaarInboxSnapshot.sellerReady;
  const buyerInboxReady = bazaarInboxSnapshot.buyerReady;
  const [messageCenterOpen, setMessageCenterOpen] = useState(false);
  const [messageCenterTab, setMessageCenterTab] = useState<'active' | 'archived'>('active');
  const [messageCenterSyncing, setMessageCenterSyncing] = useState(false);
  const bazaarPortalReady = !!portalTarget;


  const [report, setReport] = useState<ReportState>({ open: false, docKey: '', item: null });
  const [reportReason, setReportReason] = useState('prohibited_item');
  const [reportDetails, setReportDetails] = useState('');
  const [reporting, setReporting] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    docKey: string;
    item: any | null;
  }>({ open: false, docKey: '', item: null });

  const [soldConfirm, setSoldConfirm] = useState<{
    open: boolean;
    docKey: string;
    item: any | null;
  }>({ open: false, docKey: '', item: null });
  const [markingSold, setMarkingSold] = useState(false);
  const [listingLifecycleBusy, setListingLifecycleBusy] = useState(false);

  useEffect(() => {
    if (!uid) {
      setSellerListings({});
      return;
    }
    return subscribeMarketplaceItemsForSeller(uid, setSellerListings);
  }, [uid]);

  useEffect(() => {
    sellerHistoryRequestRef.current += 1;
    setSellerHistory({});
    setSellerHistoryCursor(null);
    setSellerHistoryHasMore(false);
    setSellerHistoryLoaded(false);
    setSellerHistoryCount(0);
  }, [uid]);

  const loadSellerHistory = async (reset = false) => {
    if (!uid || sellerHistoryLoading) return;
    const requestId = ++sellerHistoryRequestRef.current;
    setSellerHistoryLoading(true);
    try {
      const [page, count] = await Promise.all([
        getMarketplaceSellerHistoryPage(uid, {
          pageSize: 20,
          cursor: reset ? null : sellerHistoryCursor,
        }),
        reset || !sellerHistoryLoaded
          ? getMarketplaceSellerHistoryCount(uid)
          : Promise.resolve(sellerHistoryCount),
      ]);
      if (requestId !== sellerHistoryRequestRef.current) return;
      setSellerHistory((current) => reset ? page.items : { ...current, ...page.items });
      setSellerHistoryCursor(page.cursor);
      setSellerHistoryHasMore(page.hasMore);
      setSellerHistoryCount(count);
      setSellerHistoryLoaded(true);
    } catch (error) {
      console.error('[Bazaar] seller history load failed', error);
      addToast?.('Could not load listing history.', 'error');
    } finally {
      if (requestId === sellerHistoryRequestRef.current) setSellerHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (mode !== 'mine' || myListingTab !== 'history' || sellerHistoryLoaded || sellerHistoryLoading || !uid) return;
    void loadSellerHistory(true);
  }, [mode, myListingTab, sellerHistoryLoaded, sellerHistoryLoading, uid]);

  useEffect(() => {
    // App normally starts this store before Bazaar is opened. Calling start here
    // is an idempotent safety net for deep links/direct mounts. Snapshot delivery
    // is synchronous, so existing unread/message state never paints as empty first.
    startBazaarInboxStore(uid);
    const sync = () => setBazaarInboxSnapshot(getBazaarInboxSnapshot(uid));
    sync();
    return subscribeBazaarInboxStore(sync);
  }, [uid]);

  const bazaarInboxReady = sellerInboxReady && buyerInboxReady;

  const sellerUnreadByListing = useMemo(() => {
    const counts: Record<string, number> = {};
    sellerInboxThreads.forEach((thread) => {
      if (!thread.unread || !thread.listingId) return;
      counts[thread.listingId] = (counts[thread.listingId] || 0) + 1;
    });
    return counts;
  }, [sellerInboxThreads]);

  const sellerUnreadTotal = useMemo(
    () => sellerInboxThreads.reduce((sum, thread) => sum + (thread.unread ? 1 : 0), 0),
    [sellerInboxThreads],
  );

  const buyerUnreadByListing = useMemo(() => {
    const counts: Record<string, number> = {};
    buyerInboxThreads.forEach((thread) => {
      if (!thread.unread || !thread.listingId) return;
      counts[thread.listingId] = (counts[thread.listingId] || 0) + 1;
    });
    return counts;
  }, [buyerInboxThreads]);

  const buyerUnreadTotal = useMemo(
    () => buyerInboxThreads.reduce((sum, thread) => sum + (thread.unread ? 1 : 0), 0),
    [buyerInboxThreads],
  );

  const messageCenterThreads = useMemo(() => {
    const sellerRows = sellerInboxThreads.map((thread) => ({
      key: `seller:${thread.id}`,
      role: 'seller' as const,
      thread,
      peerName: safeStr(thread.buyerName).trim() || 'Buyer',
      peerAvatar: safeStr(thread.buyerAvatar).trim(),
      unread: Boolean(thread.unread),
    }));
    const buyerRows = buyerInboxThreads.map((thread) => ({
      key: `buyer:${thread.id}`,
      role: 'buyer' as const,
      thread,
      peerName: safeStr(thread.sellerName).trim() || 'Seller',
      peerAvatar: safeStr(thread.sellerAvatar).trim(),
      unread: Boolean(thread.unread),
    }));
    return [...sellerRows, ...buyerRows].sort((a, b) =>
      Number(b.thread.lastMessageAt || b.thread.updatedAt || 0) -
      Number(a.thread.lastMessageAt || a.thread.updatedAt || 0),
    );
  }, [sellerInboxThreads, buyerInboxThreads]);

  const activeMessageThreads = useMemo(
    () => messageCenterThreads.filter((entry) => safeStr(entry.thread.status || 'active').toLowerCase() !== 'archived'),
    [messageCenterThreads],
  );
  const archivedMessageThreads = useMemo(
    () => messageCenterThreads.filter((entry) => safeStr(entry.thread.status).toLowerCase() === 'archived'),
    [messageCenterThreads],
  );
  const visibleMessageThreads = messageCenterTab === 'archived' ? archivedMessageThreads : activeMessageThreads;
  const activeMessageUnreadTotal = activeMessageThreads.reduce((sum, entry) => sum + (entry.unread ? 1 : 0), 0);

  useEffect(() => {
    if (!messageCenterOpen || !uid || messageCenterSyncing) return;
    let cancelled = false;
    setMessageCenterSyncing(true);
    void refreshMyBazaarThreadLifecycle()
      .catch((error) => console.warn('[Bazaar] message lifecycle refresh skipped', error))
      .finally(() => { if (!cancelled) setMessageCenterSyncing(false); });
    return () => { cancelled = true; };
  }, [messageCenterOpen, uid]);

  const resolveThreadItem = (thread: BazaarDMThread) => {
    const combined = { ...(marketplaceItems || {}), ...(sellerListings || {}), ...(sellerHistory || {}) } as Record<string, any>;
    const matched = Object.entries(combined).find(([key, value]) => getItemDocKey(value, key) === thread.listingId);
    if (matched) return matched[1] || {};
    return {
      docId: thread.listingId,
      title: thread.listingTitle || 'Bazaar listing',
      imageUrls: thread.listingImage ? [thread.listingImage] : [],
      images: thread.listingImage ? [thread.listingImage] : [],
      priceCents: Number(thread.listingPriceCents || 0),
      currency: thread.listingCurrency || 'usd',
      sellerId: thread.sellerId,
      sellerName: thread.sellerName || 'Seller',
      sellerAvatar: thread.sellerAvatar || '',
      status: 'active',
      quantity: 1,
    };
  };

  const openMessageCenterThread = (entry: (typeof messageCenterThreads)[number]) => {
    const thread = entry.thread;
    const item = resolveThreadItem(thread);
    setConversation({
      open: true,
      threadId: thread.id,
      listingId: thread.listingId,
      item,
      buyerId: thread.buyerId,
      buyerName: thread.buyerName || 'Buyer',
      buyerAvatar: thread.buyerAvatar || undefined,
      startWithOffer: false,
      returnTo: 'messageCenter',
    });
    setMessageCenterOpen(false);
  };

  const sellerListingCounts = useMemo(() => {
    let live = 0;
    Object.values(sellerListings || {}).forEach((item: any) => {
      const status = safeStr(item?.status || 'active').toLowerCase();
      if (['active', 'available', 'pending'].includes(status) && Number(item?.quantity ?? 1) > 0 && !item?.isSold) live += 1;
    });
    return { live, history: sellerHistoryCount };
  }, [sellerListings, sellerHistoryCount]);

  useEffect(() => {
    const userLat = Number(currentCoords?.lat);
    const userLng = Number(currentCoords?.lng ?? currentCoords?.lon);
    const token = ++proximityRefreshRef.current;

    if (!Number.isFinite(userLat) || !Number.isFinite(userLng)) {
      setDistanceByLocationKey({});
      return;
    }

    const locations = new Map<string, string>();
    Object.values(marketplaceItems || {}).forEach((item: any) => {
      const status = safeStr(item?.status || 'active').toLowerCase();
      if (item?.isSold || Number(item?.quantity ?? 1) <= 0) return;
      if (!['active', 'available', 'pending'].includes(status)) return;
      const key = marketplaceLocationKey(item);
      const query = marketplaceLocationQuery(item);
      if (key && query) locations.set(key, query);
    });

    if (!locations.size) {
      setDistanceByLocationKey({});
      return;
    }

    void (async () => {
      const next: Record<string, number> = {};
      await Promise.all(Array.from(locations.entries()).map(async ([key, query]) => {
        try {
          const rows = await forwardGeocodeServer(query, 1);
          const first = rows?.[0];
          if (!first || !Number.isFinite(first.lat) || !Number.isFinite(first.lon)) return;
          next[key] = distanceKm(userLat, userLng, Number(first.lat), Number(first.lon));
        } catch {}
      }));
      if (token !== proximityRefreshRef.current) return;
      setDistanceByLocationKey(next);
    })();

    return () => {
      if (proximityRefreshRef.current === token) proximityRefreshRef.current += 1;
    };
  }, [marketplaceItems, currentCoords?.lat, currentCoords?.lng, currentCoords?.lon]);

  const itemsList = useMemo(() => {
    const source = mode === 'mine'
      ? (myListingTab === 'history' ? sellerHistory : sellerListings)
      : marketplaceItems;
    let rows = Object.entries(source || {})
      .map(([key, value]) => ({ docKey: getItemDocKey(value, key), item: value || {} }));

    if (mode === 'mine') {
      rows = rows.filter(({ item }) => {
        if (safeStr(item?.sellerId).trim() !== uid) return false;
        const status = safeStr(item?.status || 'active').toLowerCase();
        if (myListingTab === 'live') return ['active', 'available', 'pending'].includes(status) && Number(item?.quantity ?? 1) > 0 && !item?.isSold;
        return status === 'sold' || status === 'archived' || Boolean(item?.isSold);
      });
    } else {
      rows = rows.filter(({ item }) => {
        const status = safeStr(item?.status || 'active').toLowerCase();
        return !item?.isSold && Number(item?.quantity ?? 1) > 0 && ['active', 'available', 'pending'].includes(status);
      });
    }

    const q = search.trim().toLowerCase();
    const filtered = !q
      ? rows
      : rows.filter(({ item }) =>
          [
            item?.title,
            item?.description,
            item?.sellerUsername,
            item?.sellerName,
            item?.category,
            item?.condition,
            item?.locationLabel,
            item?.city,
            item?.region,
            item?.countryCode,
          ]
            .map(safeStr)
            .join(' ')
            .toLowerCase()
            .includes(q),
        );

    return filtered.sort((a, b) => {
      if (mode === 'all') {
        const aDistance = distanceByLocationKey[marketplaceLocationKey(a.item)];
        const bDistance = distanceByLocationKey[marketplaceLocationKey(b.item)];
        const aKnown = Number.isFinite(aDistance);
        const bKnown = Number.isFinite(bDistance);
        if (aKnown && bKnown && aDistance !== bDistance) return aDistance - bDistance;
        if (aKnown !== bKnown) return aKnown ? -1 : 1;
      }
      return Number(b.item?.createdAt || 0) - Number(a.item?.createdAt || 0);
    });
  }, [marketplaceItems, sellerListings, sellerHistory, mode, myListingTab, search, uid, distanceByLocationKey]);

  useEffect(() => {
    if (!selectedListing?.docKey) return;
    const combined = { ...(marketplaceItems || {}), ...(sellerListings || {}), ...(sellerHistory || {}) } as Record<string, any>;
    const latest = Object.entries(combined).find(([key, value]) =>
      getItemDocKey(value, key) === selectedListing.docKey,
    );
    if (!latest) return;
    const [, latestItem] = latest;
    setSelectedListing((current) => {
      if (current?.docKey !== selectedListing.docKey) return current;
      const nextItem = latestItem || {};
      if (listingDetailSyncKey(current.item) === listingDetailSyncKey(nextItem)) return current;
      return { docKey: current.docKey, item: nextItem };
    });
  }, [marketplaceItems, sellerListings, sellerHistory, selectedListing?.docKey]);

  useEffect(() => {
    const listingId = safeStr(initialItemId).trim();
    const threadId = safeStr(initialThreadId).trim();
    if (!listingId || !uid) return;

    const deepLinkKey = `${listingId}::${threadId || 'listing'}`;
    if (deepLinkHandledRef.current === deepLinkKey) return;

    const combined = { ...(marketplaceItems || {}), ...(sellerListings || {}), ...(sellerHistory || {}) } as Record<string, any>;
    const matched = Object.entries(combined).find(([key, value]) =>
      getItemDocKey(value, key) === listingId,
    );
    if (!matched) return;

    const [, itemValue] = matched;
    const item = itemValue || {};
    setMode('all');
    setSearch('');
    setSelectedListing({ docKey: listingId, item });

    if (!threadId) {
      deepLinkHandledRef.current = deepLinkKey;
      return;
    }

    const sellerId = safeStr(item?.sellerId).trim();
    const isSeller = Boolean(sellerId && sellerId === uid);

    if (isSeller) {
      const sellerThread = sellerInboxThreads.find((row) => row.id === threadId) as any;
      if (!sellerThread) return;
      setConversation({
        open: true,
        threadId,
        listingId,
        item,
        buyerId: safeStr(sellerThread?.buyerId).trim(),
        buyerName: safeStr(sellerThread?.buyerName).trim() || 'Buyer',
        buyerAvatar: safeStr(sellerThread?.buyerAvatar).trim() || undefined,
        returnTo: 'listing',
      });
      deepLinkHandledRef.current = deepLinkKey;
      return;
    }

    setConversation({
      open: true,
      threadId,
      listingId,
      item,
      buyerId: uid,
      buyerName: displayName,
      buyerAvatar: avatar || undefined,
      returnTo: 'listing',
    });
    deepLinkHandledRef.current = deepLinkKey;
  }, [
    initialItemId,
    initialThreadId,
    marketplaceItems,
    sellerListings,
    sellerHistory,
    uid,
    displayName,
    avatar,
    sellerInboxThreads,
  ]);

  const resetCreate = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setCurrency('usd');
    setCategory('tackle');
    setCondition('good');
    setCity('');
    setRegion('');
    setCountryCode('US');
    setLocalPickup(true);
    setShipping(false);
    photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    setPhotoFiles([]);
    setPhotoPreviews([]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const normalizePickedImageFiles = (files: FileList | File[] | null | undefined) =>
    Array.from(files || []).filter((file) => file.type.startsWith('image/'));

  const getNativePhotoExtension = (format?: string | null, mimeType?: string | null) => {
    const fmt = String(format || '').toLowerCase();
    const mime = String(mimeType || '').toLowerCase();
    if (fmt.includes('png') || mime.includes('png')) return 'png';
    if (fmt.includes('webp') || mime.includes('webp')) return 'webp';
    if (fmt.includes('heic') || mime.includes('heic')) return 'heic';
    if (fmt.includes('heif') || mime.includes('heif')) return 'heif';
    return 'jpg';
  };

  const isNativePickerCancel = (error: unknown) => {
    const message = String((error as any)?.message || error || '').toLowerCase();
    return (
      message.includes('cancel') ||
      message.includes('cancelled') ||
      message.includes('canceled') ||
      message.includes('user denied') ||
      message.includes('no image picked')
    );
  };

  const nativePhotoToFile = async (
    photo: { webPath?: string | null; path?: string | null; format?: string | null },
    index = 0,
  ) => {
    const webPath = photo.webPath || photo.path;
    if (!webPath) throw new Error('Photo picker did not return an image path.');

    const response = await fetch(webPath);
    if (!response.ok) throw new Error(`Photo picker could not read the selected image (${response.status}).`);

    const blob = await response.blob();
    const mimeType =
      blob.type ||
      (String(photo.format || '').toLowerCase().includes('png') ? 'image/png' : 'image/jpeg');
    const ext = getNativePhotoExtension(photo.format, mimeType);

    return new File(
      [blob],
      `roddo-bazaar-${Date.now()}-${index}.${ext}`,
      { type: mimeType },
    );
  };

  const onPickPhotos = (files: FileList | File[] | null) => {
    photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    const next = normalizePickedImageFiles(files).slice(0, MAX_LISTING_IMAGES);
    setPhotoFiles(next);
    setPhotoPreviews(next.map((file) => URL.createObjectURL(file)));
  };

  const chooseCreatePhotos = async () => {
    if (!Capacitor.isNativePlatform()) {
      fileRef.current?.click();
      return;
    }

    try {
      const result = await Camera.pickImages({
        quality: 86,
        width: 1800,
        limit: MAX_LISTING_IMAGES,
      });
      const files = await Promise.all(
        (result.photos || []).map((photo, index) => nativePhotoToFile(photo, index)),
      );
      onPickPhotos(files);
    } catch (error) {
      if (isNativePickerCancel(error)) return;
      console.error('[Bazaar] native create photo picker failed', error);
      addToast?.('Photo library had a hiccup. Please try again.', 'warning');
    }
  };

  const createListing = async () => {
    if (!uid) return addToast?.('Sign in to list an item.', 'error');
    const cleanTitle = title.trim();
    const priceCents = priceInputToCents(price);
    if (!cleanTitle) return addToast?.('Add a listing title.', 'error');
    if (!Number.isFinite(priceCents) || priceCents <= 0) return addToast?.('Enter a valid price greater than zero.', 'error');
    if (!localPickup && !shipping) return addToast?.('Choose local pickup, shipping, or both.', 'error');
    if (!city.trim()) return addToast?.('Add a city or pickup area. Exact GPS is never required.', 'error');
    if (creating) return;

    setCreating(true);
    const listingId = listingIdNow();
    const uploadedPaths: string[] = [];
    let listingCreated = false;

    try {
      // Create the authoritative listing first. This gives the listing a stable
      // owner/id before any listing-scoped Storage assets are attached.
      await createMarketplaceItem({
        id: Number(listingId),
        docId: listingId,
        title: cleanTitle,
        description: description.trim() || undefined,
        priceCents,
        currency,
        category,
        condition,
        quantity: 1,
        imageUrls: [],
        images: [],
        imagePaths: [],
        sellerId: uid,
        sellerName: displayName,
        sellerUsername: safeStr((currentUserProfile as any)?.username).trim() || undefined,
        sellerAvatar: avatar || undefined,
        city: city.trim(),
        region: region.trim() || undefined,
        countryCode: countryCode.trim().toUpperCase().slice(0, 2),
        locationLabel: buildLocationLabel(city, region, countryCode),
        fulfillmentMethods: [
          ...(localPickup ? ['LOCAL_PICKUP'] : []),
          ...(shipping ? ['SHIPPING'] : []),
        ] as any,
        checkoutMethods: ['DIRECT'],
      } as any);
      listingCreated = true;

      if (photoFiles.length) {
        const uploads = [] as Array<{ url: string; path: string }>;
        try {
          for (let i = 0; i < photoFiles.length; i += 1) {
            const result = await uploadMarketplaceImage(photoFiles[i], listingId, i);
            uploads.push(result);
            uploadedPaths.push(result.path);
          }

          await updateMarketplaceItem(listingId, {
            imageUrls: uploads.map((row) => row.url),
            images: uploads.map((row) => row.url),
            imagePaths: uploads.map((row) => row.path),
          } as any);
        } catch (imageError: any) {
          console.error('[Bazaar] listing image upload failed', {
            error: imageError,
            code: imageError?.code,
            message: imageError?.message,
            uid,
            listingId,
            photoCount: photoFiles.length,
            photoTypes: photoFiles.map((file) => file.type),
            photoSizes: photoFiles.map((file) => file.size),
          });
          if (uploadedPaths.length) await deleteMarketplaceImages(uploadedPaths).catch(() => {});
          const code = String(imageError?.code || '');
          const message = String(imageError?.message || '');
          addToast?.(
            code.includes('storage/unauthorized') || code.includes('storage/unauthenticated')
              ? 'Listing created, but your session could not upload the photo. Sign out and back in, then add it from Edit listing.'
              : message.includes('IMAGE_UPLOAD_TOO_LARGE')
                ? 'Listing created, but that photo could not be reduced enough to upload. Add a smaller photo from Edit listing.'
                : 'Listing created, but the photo upload failed. Open Edit listing and try the photo again.',
            'error',
          );
        }
      }

      addToast?.('Listing is live.', 'success');
      setCreateOpen(false);
      resetCreate();
    } catch (error) {
      console.error('[Bazaar] create failed', error);
      if (uploadedPaths.length) await deleteMarketplaceImages(uploadedPaths).catch(() => {});
      addToast?.(
        listingCreated
          ? 'The listing was created, but could not be finished.'
          : 'Could not create the listing.',
        'error',
      );
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (docKey: string, item: any) => {
    const methods = Array.isArray(item?.fulfillmentMethods) ? item.fulfillmentMethods : ['LOCAL_PICKUP'];
    setEdit({ open: true, docKey, item });
    setEditTitle(safeStr(item?.title || item?.name));
    setEditDescription(safeStr(item?.description));
    setEditPrice((Math.max(0, Number(item?.priceCents || 0)) / 100).toFixed(2));
    setEditCurrency(safeStr(item?.currency || 'usd').toLowerCase());
    setEditCategory(safeStr(item?.category || 'other'));
    setEditCondition(safeStr(item?.condition || 'good'));
    setEditCity(safeStr(item?.city));
    setEditRegion(safeStr(item?.region));
    setEditCountryCode(safeStr(item?.countryCode || 'US').toUpperCase());
    setEditLocalPickup(methods.includes('LOCAL_PICKUP'));
    setEditShipping(methods.includes('SHIPPING'));
    const urls = pickImages(item);
    const paths = Array.isArray(item?.imagePaths) ? item.imagePaths.map((value: any) => safeStr(value).trim()) : [];
    setEditPhotoOrder((current) => {
      current.forEach((slot) => {
        if (slot.kind === 'new') URL.revokeObjectURL(slot.url);
      });
      return urls.map((url, index) => ({
        id: `existing_${index}_${url}`,
        kind: 'existing' as const,
        url,
        path: paths[index] || undefined,
      }));
    });
    editPhotoDragIdRef.current = '';
    setEditPhotoDragId('');
    if (editFileRef.current) editFileRef.current.value = '';
  };

  const onPickEditPhotos = (files: FileList | File[] | null) => {
    const selected = normalizePickedImageFiles(files);
    if (!selected.length) return;

    setEditPhotoOrder((current) => {
      const available = Math.max(0, MAX_LISTING_IMAGES - current.length);
      const accepted = selected.slice(0, available);
      const stamp = Date.now();
      return [
        ...current,
        ...accepted.map((file, index) => ({
          id: `new_${stamp}_${index}_${file.name}`,
          kind: 'new' as const,
          url: URL.createObjectURL(file),
          file,
        })),
      ];
    });

    if (editFileRef.current) editFileRef.current.value = '';
  };

  const chooseEditPhotos = async () => {
    const available = Math.max(0, MAX_LISTING_IMAGES - editPhotoOrder.length);
    if (available <= 0) return;

    if (!Capacitor.isNativePlatform()) {
      editFileRef.current?.click();
      return;
    }

    try {
      const result = await Camera.pickImages({
        quality: 86,
        width: 1800,
        limit: available,
      });
      const files = await Promise.all(
        (result.photos || []).map((photo, index) => nativePhotoToFile(photo, index)),
      );
      onPickEditPhotos(files);
    } catch (error) {
      if (isNativePickerCancel(error)) return;
      console.error('[Bazaar] native edit photo picker failed', error);
      addToast?.('Photo library had a hiccup. Please try again.', 'warning');
    }
  };

  const removeEditPhoto = (id: string) => {
    setEditPhotoOrder((rows) => {
      const target = rows.find((row) => row.id === id);
      if (target?.kind === 'new') URL.revokeObjectURL(target.url);
      return rows.filter((row) => row.id !== id);
    });
  };

  const clearNewEditPhotos = () => {
    setEditPhotoOrder((rows) => {
      rows.forEach((row) => {
        if (row.kind === 'new') URL.revokeObjectURL(row.url);
      });
      return rows.filter((row) => row.kind === 'existing');
    });
    if (editFileRef.current) editFileRef.current.value = '';
  };

  const moveEditPhoto = (sourceId: string, targetId: string) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setEditPhotoOrder((rows) => {
      const fromIndex = rows.findIndex((row) => row.id === sourceId);
      const toIndex = rows.findIndex((row) => row.id === targetId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return rows;
      const next = [...rows];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const beginEditPhotoDrag = (event: React.PointerEvent<HTMLDivElement>, id: string) => {
    if (editSaving) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    editPhotoDragIdRef.current = id;
    setEditPhotoDragId(id);
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch {}
  };

  const moveEditPhotoDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const sourceId = editPhotoDragIdRef.current;
    if (!sourceId) return;
    const target = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-bazaar-photo-id]') as HTMLElement | null;
    const targetId = target?.dataset?.bazaarPhotoId || '';
    if (targetId && targetId !== sourceId) moveEditPhoto(sourceId, targetId);
  };

  const endEditPhotoDrag = () => {
    editPhotoDragIdRef.current = '';
    setEditPhotoDragId('');
  };

  const saveEdit = async () => {
    const priceCents = priceInputToCents(editPrice);
    if (!edit.docKey || !editTitle.trim()) return addToast?.('Add a listing title.', 'error');
    if (!Number.isFinite(priceCents) || priceCents <= 0) return addToast?.('Enter a valid price greater than zero.', 'error');
    if (!editLocalPickup && !editShipping) return addToast?.('Choose at least one fulfillment method.', 'error');
    if (!editCity.trim()) return addToast?.('Add a city or pickup area.', 'error');

    setEditSaving(true);
    const originalPaths = Array.isArray(edit.item?.imagePaths) ? edit.item.imagePaths.map((value: any) => safeStr(value).trim()) : [];
    const retainedPaths = new Set(
      editPhotoOrder
        .filter((row): row is Extract<EditPhotoSlot, { kind: 'existing' }> => row.kind === 'existing')
        .map((row) => safeStr(row.path).trim())
        .filter(Boolean),
    );
    const removedPaths = originalPaths.filter((path: string) => path && !retainedPaths.has(path));
    const newUploads: Array<{ id: string; url: string; path: string }> = [];

    try {
      for (let index = 0; index < editPhotoOrder.length; index += 1) {
        const slot = editPhotoOrder[index];
        if (slot.kind !== 'new') continue;
        const result = await uploadMarketplaceImage(slot.file, edit.docKey, index);
        newUploads.push({ id: slot.id, ...result });
      }

      const uploadedById = new Map(newUploads.map((row) => [row.id, row]));
      const orderedSaved = editPhotoOrder.map((slot) => {
        if (slot.kind === 'existing') return { url: slot.url, path: slot.path };
        const uploaded = uploadedById.get(slot.id);
        if (!uploaded) throw new Error('A Bazaar photo upload did not finish.');
        return { url: uploaded.url, path: uploaded.path };
      }).slice(0, MAX_LISTING_IMAGES);
      const nextImages = orderedSaved.map((row) => row.url);
      const nextPaths = orderedSaved.map((row) => row.path).filter((value): value is string => Boolean(value));

      await updateMarketplaceItem(edit.docKey, {
        title: editTitle.trim(),
        description: editDescription.trim() || undefined,
        priceCents,
        currency: editCurrency,
        category: editCategory,
        condition: editCondition,
        city: editCity.trim(),
        region: editRegion.trim() || undefined,
        countryCode: editCountryCode.trim().toUpperCase().slice(0, 2),
        locationLabel: buildLocationLabel(editCity, editRegion, editCountryCode),
        fulfillmentMethods: [
          ...(editLocalPickup ? ['LOCAL_PICKUP'] : []),
          ...(editShipping ? ['SHIPPING'] : []),
        ] as any,
        imageUrls: nextImages,
        images: nextImages,
        imagePaths: nextPaths,
      } as any);

      if (removedPaths.length) await deleteMarketplaceImages(removedPaths).catch((error) => console.warn('[Bazaar] removed image cleanup skipped', error));
      editPhotoOrder.forEach((slot) => {
        if (slot.kind === 'new') URL.revokeObjectURL(slot.url);
      });
      setEditPhotoOrder([]);
      addToast?.('Listing updated.', 'success');
      setEdit({ open: false, docKey: '', item: null });
    } catch (error) {
      if (newUploads.length) await deleteMarketplaceImages(newUploads.map((row) => row.path)).catch(() => {});
      console.error('[Bazaar] edit failed', error);
      addToast?.('Could not update the listing.', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const archiveListing = async (docKey: string) => {
    if (!window.confirm('Archive this listing? It will disappear from the active Bazaar but remain available for audit/history.')) return;
    try {
      await archiveMarketplaceItem(docKey);
      addToast?.('Listing archived.', 'success');
      setMode('mine');
      setMyListingTab('history');
      setSellerHistoryLoaded(false);
      setSelectedListing((current) => current?.docKey === docKey ? null : current);
    } catch (error) {
      console.error('[Bazaar] archive failed', error);
      addToast?.('Could not archive the listing.', 'error');
    }
  };

  const markListingPending = async (docKey: string) => {
    if (listingLifecycleBusy || !docKey) return;
    setListingLifecycleBusy(true);
    try { await sellerSetMarketplaceItemPending(docKey); addToast?.('Listing marked pending.', 'success'); setMode('mine'); setMyListingTab('live'); }
    catch (error: any) { console.error('[Bazaar] mark pending failed', error); addToast?.(safeStr(error?.message) || 'Could not mark the listing pending.', 'error'); }
    finally { setListingLifecycleBusy(false); }
  };

  const makeListingAvailable = async (docKey: string) => {
    if (listingLifecycleBusy || !docKey) return;
    setListingLifecycleBusy(true);
    try { await sellerMakeMarketplaceItemAvailable(docKey); addToast?.('Listing is available again.', 'success'); setMode('mine'); setMyListingTab('live'); }
    catch (error: any) { console.error('[Bazaar] make available failed', error); addToast?.(safeStr(error?.message) || 'Could not make the listing available.', 'error'); }
    finally { setListingLifecycleBusy(false); }
  };

  const requestMarkSold = (docKey: string, item: any) => {
    if (markingSold) return;
    setSoldConfirm({ open: true, docKey, item });
  };

  const markListingSold = async () => {
    if (markingSold || !soldConfirm.docKey) return;
    const docKey = soldConfirm.docKey;
    setMarkingSold(true);
    try {
      await sellerMarkMarketplaceItemSold(docKey);
      addToast?.('Listing marked sold.', 'success');
      setSoldConfirm({ open: false, docKey: '', item: null });
      setMode('mine');
      setMyListingTab('history');
      setSellerHistoryLoaded(false);
      setSelectedListing((current) => current?.docKey === docKey ? null : current);
    } catch (error: any) {
      console.error('[Bazaar] mark sold failed', error);
      addToast?.(safeStr(error?.message) || 'Could not mark the listing sold.', 'error');
    } finally {
      setMarkingSold(false);
    }
  };

  const requestDeleteListing = (docKey: string, item: any) => {
    if (deletingListing) return;
    setDeleteConfirm({ open: true, docKey, item });
  };

  const deleteListing = async () => {
    if (deletingListing || !deleteConfirm.docKey) return;
    const docKey = deleteConfirm.docKey;
    setDeletingListing(true);
    try {
      await sellerDeleteMarketplaceItem(docKey);
      addToast?.('Listing deleted.', 'success');
      setSelectedListing((current) => current?.docKey === docKey ? null : current);
      if (edit.docKey === docKey) setEdit({ open: false, docKey: '', item: null });
    } catch (error: any) {
      console.error('[Bazaar] delete failed', error);
      const message = safeStr(error?.message);
      addToast?.(message.includes('transaction') ? 'This listing has transaction history and must be archived instead.' : 'Could not delete the listing.', 'error');
    } finally {
      setDeletingListing(false);
    }
  };

  const openBuyerConversation = (docKey: string, item: any) => {
    if (!uid) return addToast?.('Sign in to message a seller.', 'error');
    const sellerId = safeStr(item?.sellerId).trim();
    if (!sellerId || sellerId === uid) return;
    setConversation({
      open: true,
      threadId: getBazaarDMThreadId(docKey, uid),
      listingId: docKey,
      item,
      buyerId: uid,
      buyerName: displayName,
      buyerAvatar: avatar || undefined,
      startWithOffer: false,
      returnTo: 'listing',
    });
  };

  const openBuyerOffer = (docKey: string, item: any) => {
    if (!uid) return addToast?.('Sign in to make an offer.', 'error');
    const sellerId = safeStr(item?.sellerId).trim();
    if (!sellerId || sellerId === uid) return;
    setConversation({
      open: true,
      threadId: getBazaarDMThreadId(docKey, uid),
      listingId: docKey,
      item,
      buyerId: uid,
      buyerName: displayName,
      buyerAvatar: avatar || undefined,
      startWithOffer: true,
      returnTo: 'listing',
    });
  };

  const openSellerMessages = (docKey: string, item: any) => {
    setSellerMessagesOpen({ open: true, docKey, item });
  };

  useEffect(() => {
    if (!sellerMessagesOpen.open || !sellerMessagesOpen.docKey || !uid) {
      setSellerThreads([]);
      setSellerThreadsLoading(false);
      return;
    }

    setSellerThreadsLoading(true);
    const unsubscribe = subscribeBazaarThreadsForListing(
      sellerMessagesOpen.docKey,
      uid,
      (rows) => {
        setSellerThreads(rows);
        setSellerThreadsLoading(false);
      },
      (error) => {
        console.error('[Bazaar] seller messages failed', error);
        setSellerThreadsLoading(false);
        addToast?.('Could not load Bazaar messages.', 'error');
      },
    );
    return unsubscribe;
  }, [sellerMessagesOpen.open, sellerMessagesOpen.docKey, uid, addToast]);

  const openSellerConversation = (thread: BazaarDMThread) => {
    setConversation({
      open: true,
      threadId: thread.id,
      listingId: thread.listingId,
      item: sellerMessagesOpen.item,
      buyerId: thread.buyerId,
      buyerName: thread.buyerName,
      buyerAvatar: thread.buyerAvatar,
      returnTo: 'sellerMessages',
    });
    setSellerMessagesOpen({ open: false, docKey: '', item: null });
  };

  const applyConversationListingStatus = (status: 'active' | 'pending' | 'sold') => {
    const listingId = conversation.listingId;
    if (!listingId) return;

    const patchItem = (item: any) => {
      const next = { ...(item || {}), status };
      if (status === 'sold') {
        next.isSold = true;
        next.quantity = 0;
      } else {
        next.isSold = false;
        if (Number(next.quantity || 0) <= 0) next.quantity = 1;
      }
      return next;
    };

    setConversation((current) =>
      current.listingId === listingId ? { ...current, item: patchItem(current.item) } : current,
    );
    setSelectedListing((current) =>
      current?.docKey === listingId ? { ...current, item: patchItem(current.item) } : current,
    );
    setSellerMessagesOpen((current) =>
      current.docKey === listingId ? { ...current, item: patchItem(current.item) } : current,
    );
  };

  const closeConversationSmart = () => {
    const target = conversation.returnTo || 'listing';
    const listingItem = conversation.item;
    const listingId = conversation.listingId;
    setConversation({ open: false, threadId: '', listingId: '', item: null, buyerId: '', buyerName: '', returnTo: 'listing' });
    if (target === 'messageCenter') {
      setMessageCenterOpen(true);
      return;
    }
    if (target === 'sellerMessages' && listingId && listingItem) {
      setSellerMessagesOpen({ open: true, docKey: listingId, item: listingItem });
    }
  };

  const shareListing = async (docKey: string) => {
    const listingId = safeStr(docKey).trim();
    if (!listingId) return;
    const result = await share({ url: buildBazaarListingShareUrl(listingId) });
    if (result.method === 'clipboard' && result.ok) addToast?.('Listing link copied.', 'success');
    else if (!result.ok) addToast?.('Could not share that listing.', 'error');
  };

  const submitReport = async () => {
    if (!uid) return addToast?.('Sign in to report a listing.', 'error');
    if (!report.item || !report.docKey) return;
    setReporting(true);
    try {
      await reportMarketplaceItem({
        itemId: report.docKey,
        sellerId: safeStr(report.item?.sellerId),
        reason: reportReason,
        details: reportDetails,
      });
      addToast?.('Report sent for review.', 'success');
      setReport({ open: false, docKey: '', item: null });
      setReportDetails('');
    } catch (error) {
      console.error('[Bazaar] report failed', error);
      addToast?.('Could not send the report.', 'error');
    } finally {
      setReporting(false);
    }
  };

  const bazaarVars = {
    '--bazaar-page-bg': ui.pageBg ?? ui.surfaceBg ?? theme.surface,
    '--bazaar-card-bg': ui.cardBgStrong ?? ui.cardBg ?? theme.panel,
    '--bazaar-surface-bg': ui.cardBgMuted ?? ui.surfaceBgSoft ?? ui.cardBg ?? theme.surfaceElevated,
    '--bazaar-input-bg': ui.inputBg ?? (isDarkMode ? theme.panelStrong : theme.surfaceElevated),
    '--bazaar-text': ui.textPrimary ?? theme.text,
    '--bazaar-text-soft': ui.textSecondary ?? theme.textSoft,
    '--bazaar-border': ui.border ?? theme.border,
    '--bazaar-border-strong': ui.borderStrong ?? ui.border ?? theme.border,
    '--bazaar-accent': '#7657E8',
    '--bazaar-gold': '#F2B84B',
    '--bazaar-modal-bg': isDarkMode ? '#070B14' : '#FFFDF8',
    '--bazaar-field-bg': isDarkMode ? '#0D1422' : '#FFFFFF',
    '--bazaar-field-border': isDarkMode ? 'rgba(226,232,240,0.34)' : 'rgba(74,58,42,0.22)',
  } as React.CSSProperties;

  const modalClass =
    'roddo-safe-modal-frame w-full max-w-lg overflow-y-auto rounded-[20px] border border-[var(--bazaar-field-border)] bg-[var(--bazaar-modal-bg)] p-4 text-[var(--bazaar-text)] shadow-[0_26px_80px_rgba(0,0,0,0.42)]';
  const inputClass =
    'mt-1 w-full rounded-[12px] border border-[var(--bazaar-field-border)] bg-[var(--bazaar-field-bg)] px-3.5 py-3 text-sm font-semibold text-[var(--bazaar-text)] placeholder:text-[var(--bazaar-text-soft)] outline-none transition focus:border-[#7657E8]/70 focus:ring-4 focus:ring-[#7657E8]/16';
  const softButtonClass =
    'rounded-[12px] border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] px-3 py-2 text-xs font-extrabold text-[var(--bazaar-text)] transition hover:-translate-y-0.5 hover:brightness-[1.03] active:translate-y-0';

  const bazaarControlStyles = `
    [data-roddo-bazaar-portal] select option {
      background: ${isDarkMode ? '#0D1422' : '#FFFFFF'};
      color: ${isDarkMode ? '#F4F6FA' : '#30271E'};
    }
  `;

  return (
    <div
      data-roddo-tour="bazaar-page"
      data-roddo-theme={isDarkMode ? 'dark' : 'light'}
      className={isDarkMode ? 'dark mx-auto h-full min-h-0 w-full overflow-y-auto overscroll-contain pb-28 lg:pb-8' : 'mx-auto h-full min-h-0 w-full overflow-y-auto overscroll-contain pb-28 lg:pb-8'}
      style={{ ...bazaarVars, backgroundColor: 'var(--bazaar-page-bg)', color: 'var(--bazaar-text)', colorScheme: isDarkMode ? 'dark' : 'light' }}
    >
      <style>{bazaarControlStyles}</style>
      <section className="border-b border-[var(--bazaar-border)] px-3 pb-5 pt-4 sm:px-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[var(--bazaar-text-soft)]">BALEEN BAY BAZAAR</div><h1 className="mt-1 text-[17px] font-black tracking-[-0.02em] text-[var(--bazaar-text)]">Find gear. Trade local. Keep it simple.</h1>
            <p className="mt-1 max-w-2xl text-xs leading-5 text-[var(--bazaar-text-soft)]">
              List fishing gear, arrange local pickup, or offer shipping. Payments are arranged directly with the seller while Roddo Checkout stays in review.
            </p>
          </div>
          <div className="flex shrink-0 items-start gap-2">
            <div className="flex flex-col items-stretch gap-1.5">
              <button type="button" onClick={() => { resetCreate(); setCreateOpen(true); }} className="rounded-[13px] bg-[#F2B84B] px-4 py-2.5 text-xs font-black text-[#2D2615] shadow-[0_8px_18px_rgba(242,184,75,.20)] transition hover:-translate-y-0.5 active:translate-y-0">
                List item
              </button>
              <button
                type="button"
                onClick={() => { setMessageCenterTab('active'); setMessageCenterOpen(true); }}
                className="relative self-end rounded-[12px] border border-[#4EA5E9]/35 bg-[#4EA5E9]/14 px-3 py-1.5 text-lg leading-none text-[#246FAD] transition hover:-translate-y-0.5 hover:bg-[#4EA5E9]/22 active:translate-y-0 dark:text-sky-200"
                aria-label="Open Bazaar messages"
                title="Bazaar messages"
              >
                💬
                {bazaarInboxReady && activeMessageUnreadTotal > 0 ? <span className="absolute -right-1.5 -top-1.5 rounded-full bg-rose-600 px-1.5 py-0.5 text-[8px] font-black leading-none text-white">{activeMessageUnreadTotal}</span> : null}
              </button>
            </div>
          </div>
        </div>


        {selectedListing ? (
          <BazaarListingDetailView
            docKey={selectedListing.docKey}
            item={selectedListing.item}
            isMine={Boolean(uid && safeStr(selectedListing.item?.sellerId).trim() === uid)}
            isDarkMode={isDarkMode}
            onBack={() => setSelectedListing(null)}
            onShare={() => void shareListing(selectedListing.docKey)}
            onMessageSeller={() => openBuyerConversation(selectedListing.docKey, selectedListing.item)}
            onMakeOffer={() => openBuyerOffer(selectedListing.docKey, selectedListing.item)}
            onOpenMessages={() => openSellerMessages(selectedListing.docKey, selectedListing.item)}
            onMarkPending={() => void markListingPending(selectedListing.docKey)}
            onMakeAvailable={() => void makeListingAvailable(selectedListing.docKey)}
            onMarkSold={() => requestMarkSold(selectedListing.docKey, selectedListing.item)}
            unreadMessageCount={Boolean(uid && safeStr(selectedListing.item?.sellerId).trim() === uid)
              ? (sellerUnreadByListing[selectedListing.docKey] || 0)
              : (buyerUnreadByListing[selectedListing.docKey] || 0)}
            onEdit={() => openEdit(selectedListing.docKey, selectedListing.item)}
            onArchive={() => void archiveListing(selectedListing.docKey)}
            onDelete={() => requestDeleteListing(selectedListing.docKey, selectedListing.item)}
            onReport={() => {
              setReport({ open: true, docKey: selectedListing.docKey, item: selectedListing.item });
              setReportReason('prohibited_item');
              setReportDetails('');
            }}
            onOpenImage={setLightboxUrl}
          />
        ) : (
          <>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-2">
                {(['all', 'mine'] as Mode[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMode(value)}
                    className={`rounded-[13px] border px-3.5 py-2 text-xs font-black transition ${mode === value
                      ? (isDarkMode
                        ? 'border-[#7657E8] bg-[#7657E8] text-white shadow-[0_8px_18px_rgba(118,87,232,.18)]'
                        : 'border-[#4EA5E9] bg-[#4EA5E9] text-white shadow-[0_8px_18px_rgba(78,165,233,.20)]')
                      : 'border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] text-[var(--bazaar-text)] hover:brightness-[1.03]'}`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span>{value === 'all' ? 'All listings' : 'My listings'}</span>
                      {bazaarInboxReady && value === 'mine' && sellerUnreadTotal > 0 ? (
                        <span className="rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-black leading-none text-white">{sellerUnreadTotal}</span>
                      ) : null}
                      {bazaarInboxReady && value === 'all' && buyerUnreadTotal > 0 ? (
                        <span className="rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-black leading-none text-white">{buyerUnreadTotal}</span>
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search listings…" className="w-full rounded-[14px] border border-[var(--bazaar-border)] bg-[var(--bazaar-input-bg)] px-3.5 py-2.5 text-sm font-semibold text-[var(--bazaar-text)] placeholder:text-[var(--bazaar-text-soft)] outline-none focus:border-[#7657E8]/50 focus:ring-4 focus:ring-[#7657E8]/10 sm:w-[330px]" />
            </div>

            {mode === 'mine' ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setMyListingTab('live')}
                  className={`rounded-full border px-3 py-1.5 text-[11px] font-black transition ${myListingTab === 'live'
                    ? 'border-emerald-700 bg-emerald-700 text-white shadow-[0_6px_14px_rgba(4,120,87,.16)]'
                    : 'border-emerald-700/45 bg-transparent text-emerald-700 dark:border-emerald-400/45 dark:text-emerald-300'}`}
                >
                  Live <span className="ml-1 opacity-75">{sellerListingCounts.live}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMyListingTab('history')}
                  className={`rounded-full border px-3 py-1.5 text-[11px] font-black transition ${myListingTab === 'history'
                    ? 'border-slate-500 bg-slate-500 text-white shadow-[0_6px_14px_rgba(71,85,105,.14)]'
                    : 'border-slate-400/60 bg-transparent text-slate-600 dark:border-slate-500 dark:text-slate-300'}`}
                >
                  History <span className="ml-1 opacity-75">{sellerHistoryLoaded ? sellerListingCounts.history : '—'}</span>
                </button>
              </div>
            ) : null}

            <div className="mt-4">
              {mode === 'mine' && myListingTab === 'history' && sellerHistoryLoading && !sellerHistoryLoaded ? (
                <div className="rounded-[18px] border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] p-5 text-sm font-semibold text-[var(--bazaar-text-soft)]">Loading listing history…</div>
              ) : itemsList.length === 0 ? (
                <div className="rounded-[18px] border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] p-5 text-sm font-semibold text-[var(--bazaar-text-soft)]">
                  {mode === 'mine'
                    ? myListingTab === 'live'
                      ? 'You have no live listings.'
                      : 'You have no sold or archived listings yet.'
                    : 'No active Bazaar listings yet.'}
                </div>
              ) : (
                mode === 'mine' && myListingTab === 'history' ? (
                  <div className="space-y-2">
                    {itemsList.map(({ docKey, item }) => {
                      const images = pickImages(item);
                      const status = safeStr(item?.status || (item?.isSold ? 'sold' : 'archived')).toLowerCase();
                      const sold = status === 'sold' || Boolean(item?.isSold);
                      return (
                        <button
                          key={docKey}
                          type="button"
                          onClick={() => { setListingMenuKey(''); setSelectedListing({ docKey, item }); }}
                          className="flex w-full items-center gap-3 rounded-[14px] border border-[var(--bazaar-border)] bg-[var(--bazaar-card-bg)] px-3 py-2 text-left shadow-[0_4px_12px_rgba(89,65,37,.04)] transition hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(89,65,37,.08)]"
                        >
                          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-[10px] border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)]">
                            {images[0] ? <img src={images[0]} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-lg">🎣</div>}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[13px] font-black text-[var(--bazaar-text)]">{safeStr(item?.title || item?.name || 'Untitled')}</div>
                            <div className="mt-0.5 text-[13px] font-black text-[#17684C] dark:text-emerald-200">{formatMoney(item?.priceCents, item?.currency)}</div>
                          </div>
                          <span className={`rounded-full px-2 py-1 text-[8px] font-black uppercase tracking-[.08em] text-white ${sold ? 'bg-emerald-700' : 'bg-slate-500'}`}>{sold ? 'Sold' : 'Archived'}</span>
                          <span className="text-lg text-[var(--bazaar-text-soft)]">›</span>
                        </button>
                      );
                    })}
                    {sellerHistoryHasMore ? (
                      <button
                        type="button"
                        disabled={sellerHistoryLoading}
                        onClick={() => void loadSellerHistory(false)}
                        className="mt-3 w-full rounded-[13px] border border-slate-400/55 bg-[var(--bazaar-surface-bg)] px-4 py-3 text-xs font-black text-[var(--bazaar-text)] transition active:scale-[0.99] disabled:opacity-50"
                      >
                        {sellerHistoryLoading ? 'Loading more…' : 'Load more'}
                      </button>
                    ) : sellerHistoryLoaded && sellerHistoryCount > 0 ? (
                      <div className="pt-2 text-center text-[10px] font-bold text-[var(--bazaar-text-soft)]">All history loaded.</div>
                    ) : null}
                  </div>
                ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                  {itemsList.map(({ docKey, item }) => {
                    const mine = Boolean(uid && safeStr(item?.sellerId).trim() === uid);
                    const images = pickImages(item);
                    const unreadCount = bazaarInboxReady
                      ? (mine
                        ? (sellerUnreadByListing[docKey] || 0)
                        : (buyerUnreadByListing[docKey] || 0))
                      : 0;
                    const menuOpen = listingMenuKey === docKey;
                    return (
                      <article
                        key={docKey}
                        className="group relative overflow-hidden rounded-[18px] border border-[var(--bazaar-border)] bg-[var(--bazaar-card-bg)] text-left shadow-[0_6px_16px_rgba(89,65,37,.05)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(89,65,37,.10)]"
                      >
                        <button
                          type="button"
                          onClick={() => { setListingMenuKey(''); setSelectedListing({ docKey, item }); }}
                          className="block w-full text-left focus:outline-none focus:ring-4 focus:ring-inset focus:ring-[#7657E8]/15"
                        >
                          <div className="relative aspect-square overflow-hidden bg-[var(--bazaar-surface-bg)]">
                            {images[0] ? (
                              <img src={images[0]} alt="" className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]" />
                            ) : (
                              <div className="grid h-full place-items-center text-center">
                                <div>
                                  <div className="text-2xl">🎣</div>
                                  <div className="mt-1 text-[9px] font-black uppercase tracking-[.12em] text-[var(--bazaar-text-soft)]">No photo</div>
                                </div>
                              </div>
                            )}
                            {(mine || safeStr(item?.status).toLowerCase() === 'pending') ? (
                              <span className={`absolute left-2 top-2 rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-[.08em] text-white ${safeStr(item?.status).toLowerCase() === 'sold' ? 'bg-emerald-700' : safeStr(item?.status).toLowerCase() === 'pending' ? 'bg-amber-600' : safeStr(item?.status).toLowerCase() === 'archived' ? 'bg-slate-600' : 'bg-[#7657E8]'}`}>
                                {safeStr(item?.status).toLowerCase() === 'sold' ? 'Sold' : safeStr(item?.status).toLowerCase() === 'pending' ? 'Pending' : safeStr(item?.status).toLowerCase() === 'archived' ? 'Archived' : 'Yours'}
                              </span>
                            ) : null}
                            {unreadCount > 0 ? (
                              <span className="absolute bottom-2 left-2 rounded-full bg-rose-600 px-2 py-1 text-[9px] font-black text-white shadow">{unreadCount} unread</span>
                            ) : null}
                            {images.length > 1 ? <span className="absolute bottom-2 right-2 rounded-full bg-black/65 px-2 py-1 text-[9px] font-black text-white">{images.length} photos</span> : null}
                          </div>
                          <div className="px-3 py-2.5">
                            <div className="text-[15px] font-black leading-[1.15] text-[#17684C] dark:text-emerald-200">{formatMoney(item?.priceCents, item?.currency)}</div>
                            <div className="mt-0.5 line-clamp-2 text-[13px] font-black leading-[1.25] text-[var(--bazaar-text)]">{safeStr(item?.title || item?.name || 'Untitled')}</div>
                            <div className="mt-1.5 truncate text-[10px] font-semibold leading-[1.2] text-[var(--bazaar-text-soft)]">{safeStr(item?.locationLabel || 'Seller-selected area')}</div>
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <span className="truncate text-[9px] font-bold leading-[1.2] text-[var(--bazaar-text-soft)]">{CONDITION_LABELS[safeStr(item?.condition)] || 'Used'}</span>
                              <span className="text-sm leading-none text-[var(--bazaar-text-soft)]">›</span>
                            </div>
                          </div>
                        </button>

                        {mine ? (
                          <div className="absolute right-2 top-2 z-20">
                            <button
                              type="button"
                              onClick={() => setListingMenuKey(menuOpen ? '' : docKey)}
                              className="grid h-9 w-9 place-items-center rounded-[11px] border border-white/40 bg-black/65 text-lg font-black text-white shadow backdrop-blur"
                              aria-label="Listing actions"
                              aria-expanded={menuOpen}
                            >
                              ⋯
                            </button>
                            {menuOpen ? (
                              <div className="absolute right-0 top-11 min-w-[160px] overflow-hidden rounded-[14px] border border-[var(--bazaar-border-strong)] bg-[var(--bazaar-card-bg)] p-1.5 text-[var(--bazaar-text)] shadow-[0_18px_42px_rgba(0,0,0,.20)]">
                                {['active', 'available'].includes(safeStr(item?.status || 'active').toLowerCase()) ? (
                                  <>
                                    <button type="button" onClick={() => { setListingMenuKey(''); openEdit(docKey, item); }} className="w-full rounded-[10px] px-3 py-2 text-left text-xs font-black hover:bg-black/[.04] dark:hover:bg-white/[.06]">Edit listing</button>
                                    <button type="button" onClick={() => { setListingMenuKey(''); void archiveListing(docKey); }} className="w-full rounded-[10px] px-3 py-2 text-left text-xs font-black hover:bg-black/[.04] dark:hover:bg-white/[.06]">Archive</button>
                                  </>
                                ) : null}
                                <button type="button" onClick={() => { setListingMenuKey(''); requestDeleteListing(docKey, item); }} className="w-full rounded-[10px] px-3 py-2 text-left text-xs font-black text-rose-600 hover:bg-rose-500/10 dark:text-rose-300">Delete</button>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
                )
              )}
            </div>
          </>
        )}
      </section>

      {bazaarPortalReady && createOpen && createPortal(
        <div data-roddo-bazaar-portal="true" data-roddo-theme={isDarkMode ? 'dark' : 'light'} className={`${isDarkMode ? 'dark ' : ''}roddo-safe-overlay fixed inset-0 z-[99999] grid place-items-center overflow-y-auto bg-black/60 p-3 backdrop-blur-sm`} style={bazaarVars} onClick={() => !creating && setCreateOpen(false)}>
          <div className={modalClass} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="text-base font-black">List an item</h2><button type="button" disabled={creating} onClick={() => setCreateOpen(false)} className="h-10 w-10 rounded-full border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] text-[var(--bazaar-text)]">✕</button></div>
            <div className="mt-3 space-y-3">
              <label className="block text-xs font-bold">Title<input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Shimano reel" className={inputClass} /></label>
              <label className="block text-xs font-bold">Description<textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={3000} rows={4} placeholder="Condition, details, meetup preference…" className={inputClass} /></label>
              <div className="grid grid-cols-[1fr_120px] gap-2"><label className="block text-xs font-bold">Price<input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" placeholder="45.00" className={inputClass} /></label><label className="block text-xs font-bold">Currency<select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputClass}>{MARKETPLACE_CURRENCIES.map((code) => <option key={code} value={code}>{code.toUpperCase()}</option>)}</select></label></div>
              <div className="grid grid-cols-2 gap-2"><label className="block text-xs font-bold">Category<select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>{MARKETPLACE_CATEGORIES.map((value) => <option key={value} value={value}>{CATEGORY_LABELS[value]}</option>)}</select></label><label className="block text-xs font-bold">Condition<select value={condition} onChange={(e) => setCondition(e.target.value)} className={inputClass}>{MARKETPLACE_CONDITIONS.map((value) => <option key={value} value={value}>{CONDITION_LABELS[value]}</option>)}</select></label></div>
              <div className="rounded-[14px] border border-[var(--bazaar-field-border)] bg-[var(--bazaar-field-bg)] p-3"><div className="text-xs font-black">Approximate item location</div><div className="mt-1 text-[11px] text-[var(--bazaar-text-soft)]">Enter a city/area. Do not enter a home address, fishing spot, or live GPS.</div><div className="mt-2 grid grid-cols-2 gap-2"><input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City / area" className={inputClass} /><input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="State / region" className={inputClass} /></div><input value={countryCode} onChange={(e) => setCountryCode(e.target.value.toUpperCase().slice(0, 2))} placeholder="Country code (US)" maxLength={2} className={inputClass} /></div>
              <div className="rounded-[14px] border border-[var(--bazaar-field-border)] bg-[var(--bazaar-field-bg)] p-3"><div className="text-xs font-black">Fulfillment</div><label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={localPickup} onChange={(e) => setLocalPickup(e.target.checked)} /> Local pickup</label><label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={shipping} onChange={(e) => setShipping(e.target.checked)} /> Shipping available</label><div className="mt-2 text-[11px] text-[var(--bazaar-text-soft)]">For now, payment and shipping arrangements remain between buyer and seller. Roddo Checkout stays disabled.</div></div>
              <div><div className="text-xs font-bold">Photos · max {MAX_LISTING_IMAGES}</div><input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPickPhotos(e.target.files)} /><div className="mt-2 flex gap-2"><button type="button" onClick={() => void chooseCreatePhotos()} className={softButtonClass}>Choose photos</button>{photoFiles.length ? <button type="button" onClick={() => onPickPhotos(null)} className="rounded-[12px] bg-rose-500/10 px-3 py-2 text-xs font-extrabold text-rose-700 dark:text-rose-300">Clear</button> : null}</div>{photoPreviews.length ? <div className="mt-2 grid grid-cols-3 gap-2">{photoPreviews.map((src, index) => <img key={`${src}_${index}`} src={src} alt="Preview" className="h-24 w-full rounded-xl object-cover" />)}</div> : null}</div>
              <div className="rounded-2xl bg-amber-50 p-3 text-[11px] font-semibold leading-5 text-amber-950 dark:bg-amber-950/20 dark:text-amber-100">List lawful fishing/outdoor gear only. Roddo may remove unsafe, prohibited, counterfeit, recalled, or regulated items.</div>
              <button type="button" disabled={creating} onClick={createListing} className="w-full rounded-[12px] bg-[#F2B84B] px-5 py-3 text-sm font-black text-[#2D2615] disabled:opacity-50">{creating ? 'Creating…' : 'Create listing'}</button>
            </div>
          </div>
        </div>,
        portalTarget!,
      )}

      {bazaarPortalReady && edit.open && createPortal(
        <div data-roddo-bazaar-portal="true" data-roddo-theme={isDarkMode ? 'dark' : 'light'} className={`${isDarkMode ? 'dark ' : ''}roddo-safe-overlay fixed inset-0 z-[99999] grid place-items-center overflow-y-auto bg-black/60 p-3 backdrop-blur-sm`} style={bazaarVars} onClick={() => !editSaving && setEdit({ open: false, docKey: '', item: null })}>
          <div className={modalClass} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-black">Edit listing</h2><button type="button" onClick={() => setEdit({ open: false, docKey: '', item: null })} className="h-10 w-10 rounded-full border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] text-[var(--bazaar-text)]">✕</button></div>
            <div className="mt-3 space-y-3">
              <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} maxLength={120} className={inputClass} />
              <textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} maxLength={3000} rows={4} className={inputClass} />
              <div className="grid grid-cols-[1fr_120px] gap-2"><input value={editPrice} onChange={(e) => setEditPrice(e.target.value)} inputMode="decimal" className={inputClass} /><select value={editCurrency} onChange={(e) => setEditCurrency(e.target.value)} className={inputClass}>{MARKETPLACE_CURRENCIES.map((code) => <option key={code} value={code}>{code.toUpperCase()}</option>)}</select></div>
              <div className="grid grid-cols-2 gap-2"><select value={editCategory} onChange={(e) => setEditCategory(e.target.value)} className={inputClass}>{MARKETPLACE_CATEGORIES.map((value) => <option key={value} value={value}>{CATEGORY_LABELS[value]}</option>)}</select><select value={editCondition} onChange={(e) => setEditCondition(e.target.value)} className={inputClass}>{MARKETPLACE_CONDITIONS.map((value) => <option key={value} value={value}>{CONDITION_LABELS[value]}</option>)}</select></div>
              <div className="grid grid-cols-2 gap-2"><input value={editCity} onChange={(e) => setEditCity(e.target.value)} placeholder="City / area" className={inputClass} /><input value={editRegion} onChange={(e) => setEditRegion(e.target.value)} placeholder="State / region" className={inputClass} /></div><input value={editCountryCode} onChange={(e) => setEditCountryCode(e.target.value.toUpperCase().slice(0, 2))} maxLength={2} className={inputClass} />
              <div className="rounded-[14px] border border-[var(--bazaar-field-border)] bg-[var(--bazaar-field-bg)] p-3"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editLocalPickup} onChange={(e) => setEditLocalPickup(e.target.checked)} /> Local pickup</label><label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={editShipping} onChange={(e) => setEditShipping(e.target.checked)} /> Shipping available</label></div>
              <div className="rounded-[14px] border border-[var(--bazaar-field-border)] bg-[var(--bazaar-field-bg)] p-3">
                <div className="flex items-start justify-between gap-2"><div><div className="text-xs font-black">Photos</div><div className="mt-1 text-[11px] text-[var(--bazaar-text-soft)]">Drag to reorder · First photo is the cover · Maximum {MAX_LISTING_IMAGES}.</div></div><span className="text-[11px] font-bold text-[var(--bazaar-text-soft)]">{editPhotoOrder.length}/{MAX_LISTING_IMAGES}</span></div>
                {editPhotoOrder.length ? (
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {editPhotoOrder.map((slot, index) => (
                      <div
                        key={slot.id}
                        data-bazaar-photo-id={slot.id}
                        onPointerDown={(event) => beginEditPhotoDrag(event, slot.id)}
                        onPointerMove={moveEditPhotoDrag}
                        onPointerUp={endEditPhotoDrag}
                        onPointerCancel={endEditPhotoDrag}
                        className={`relative select-none overflow-hidden rounded-xl border transition ${editPhotoDragId === slot.id ? 'scale-[1.03] border-[#7657E8] shadow-lg ring-2 ring-[#7657E8]/25' : slot.kind === 'new' ? 'border-emerald-500/30' : 'border-black/10'}`}
                        style={{ touchAction: 'none', cursor: editSaving ? 'default' : 'grab' }}
                        aria-label={`Photo ${index + 1}${index === 0 ? ', cover photo' : ''}. Drag to reorder.`}
                      >
                        <img src={slot.url} alt={`Listing ${index + 1}`} className="h-24 w-full pointer-events-none object-cover" />
                        <div className="pointer-events-none absolute left-1 top-1 flex items-center gap-1">
                          <span className="rounded-full bg-black/70 px-2 py-0.5 text-[9px] font-black text-white">{index + 1}</span>
                          {index === 0 ? <span className="rounded-full bg-[#F2B84B] px-2 py-0.5 text-[9px] font-black text-[#2D2615]">COVER</span> : null}
                        </div>
                        {slot.kind === 'new' ? <span className="pointer-events-none absolute bottom-1 left-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[9px] font-black text-white">NEW</span> : null}
                        <button type="button" disabled={editSaving} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); removeEditPhoto(slot.id); }} className="absolute right-1 top-1 h-7 w-7 rounded-full bg-black/75 text-xs font-black text-white" aria-label="Remove photo">✕</button>
                      </div>
                    ))}
                  </div>
                ) : <div className="mt-3 rounded-xl border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] p-3 text-xs font-semibold text-[var(--bazaar-text-soft)]">No saved photos.</div>}
                <input ref={editFileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPickEditPhotos(e.target.files)} />
                <div className="mt-3 flex flex-wrap gap-2"><button type="button" disabled={editSaving || editPhotoOrder.length >= MAX_LISTING_IMAGES} onClick={() => void chooseEditPhotos()} className="rounded-[12px] border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] px-3 py-2 text-xs font-extrabold text-[var(--bazaar-text)] disabled:opacity-40">Add photos</button>{editPhotoOrder.some((row) => row.kind === 'new') ? <button type="button" disabled={editSaving} onClick={clearNewEditPhotos} className="rounded-[12px] bg-rose-500/10 px-3 py-2 text-xs font-extrabold text-rose-700 dark:text-rose-300">Clear new photos</button> : null}</div>
              </div>
              <button type="button" disabled={editSaving} onClick={saveEdit} className="w-full rounded-[12px] bg-[#7657E8] px-4 py-3 text-sm font-black text-white disabled:opacity-50">{editSaving ? 'Saving…' : 'Save changes'}</button>
              <button type="button" disabled={editSaving || deletingListing} onClick={() => requestDeleteListing(edit.docKey, edit.item)} className="w-full rounded-[12px] border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm font-black text-rose-700 disabled:opacity-50 dark:text-rose-300">{deletingListing ? 'Deleting…' : 'Delete listing permanently'}</button>
            </div>
          </div>
        </div>, portalTarget!,
      )}

      {bazaarPortalReady && messageCenterOpen && createPortal(
        <div data-roddo-bazaar-portal="true" data-roddo-theme={isDarkMode ? 'dark' : 'light'} className={`${isDarkMode ? 'dark ' : ''}roddo-safe-overlay fixed inset-0 z-[99999] grid place-items-center overflow-y-auto bg-black/60 p-3 backdrop-blur-sm`} style={bazaarVars} onClick={() => setMessageCenterOpen(false)}>
          <div className={`${modalClass} max-w-xl`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-black">Bazaar messages</h2>
                <div className="mt-0.5 text-xs text-[var(--bazaar-text-soft)]">Current deals stay active. Closed listing conversations are archived.</div>
              </div>
              <button type="button" onClick={() => setMessageCenterOpen(false)} className="h-10 w-10 rounded-full border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] text-[var(--bazaar-text)]">✕</button>
            </div>
            <div className="mt-3 flex gap-2">
              {(['active', 'archived'] as const).map((tab) => {
                const count = tab === 'active' ? activeMessageThreads.length : archivedMessageThreads.length;
                return (
                  <button key={tab} type="button" onClick={() => setMessageCenterTab(tab)} className={`rounded-full border px-3 py-1.5 text-[11px] font-black transition ${messageCenterTab === tab ? 'border-[#4EA5E9]/40 bg-[#4EA5E9]/15 text-[#246FAD] dark:text-sky-200' : 'border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] text-[var(--bazaar-text-soft)]'}`}>
                    {tab === 'active' ? 'Active' : 'Archived'} <span className="ml-1 opacity-70">{count}</span>
                  </button>
                );
              })}
              {messageCenterSyncing ? <span className="self-center text-[10px] font-bold text-[var(--bazaar-text-soft)]">Syncing…</span> : null}
            </div>
            <div className="mt-3 max-h-[60dvh] space-y-2 overflow-y-auto roddo-content-scroll">
              {visibleMessageThreads.length === 0 ? (
                <div className="rounded-[16px] border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] p-4 text-sm font-semibold text-[var(--bazaar-text-soft)]">{messageCenterTab === 'active' ? 'No active Bazaar conversations.' : 'No archived Bazaar conversations.'}</div>
              ) : visibleMessageThreads.map((entry) => {
                const thread = entry.thread;
                const image = safeStr(thread.listingImage).trim();
                return (
                  <button key={entry.key} type="button" onClick={() => openMessageCenterThread(entry)} className="w-full rounded-[14px] border border-[var(--bazaar-field-border)] bg-[var(--bazaar-field-bg)] p-3 text-left transition hover:-translate-y-0.5 hover:brightness-[1.03]">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-[12px] border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)]">
                        {image ? <img src={image} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-xs font-black">{safeStr(thread.listingTitle).slice(0, 1).toUpperCase() || 'B'}</div>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="truncate text-sm font-black">{entry.peerName}</div>
                          <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-[.08em] ${entry.role === 'seller' ? 'bg-violet-500/12 text-violet-700 dark:text-violet-200' : 'bg-[#4EA5E9]/15 text-[#246FAD] dark:text-sky-200'}`}>{entry.role === 'seller' ? 'Selling' : 'Buying'}</span>
                          {entry.unread ? <span className="rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-black text-white">NEW</span> : null}
                        </div>
                        <div className="mt-0.5 truncate text-[11px] font-black text-[var(--bazaar-text)]">{thread.listingTitle || 'Bazaar listing'}</div>
                        <div className="mt-1 truncate text-xs font-semibold text-[var(--bazaar-text-soft)]">{thread.lastMessageText || 'Open conversation'}</div>
                        <div className="mt-1 text-[9px] font-bold uppercase tracking-[.06em] text-[var(--bazaar-text-soft)]">{formatMessageStamp(thread.lastMessageAt || thread.updatedAt)}</div>
                      </div>
                      <span className="text-lg text-[var(--bazaar-text-soft)]">›</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>, portalTarget!,
      )}

      {bazaarPortalReady && conversation.open && conversation.item && createPortal(
        <div data-roddo-bazaar-portal="true" data-roddo-theme={isDarkMode ? 'dark' : 'light'} className={`${isDarkMode ? 'dark ' : ''}roddo-safe-overlay fixed inset-0 z-[99999] grid place-items-center overflow-y-auto bg-black/60 p-3 backdrop-blur-sm`} style={bazaarVars} onClick={closeConversationSmart}>
          <div className={`${modalClass} max-w-xl`} onClick={(e) => e.stopPropagation()}>
            <BazaarDMView
              threadId={conversation.threadId}
              listingId={conversation.listingId}
              listingTitle={safeStr(conversation.item?.title || conversation.item?.name || 'Bazaar listing')}
              listingImage={pickImages(conversation.item)[0]}
              listingPriceCents={Number(conversation.item?.priceCents || 0)}
              listingCurrency={safeStr(conversation.item?.currency || 'usd')}
              listingStatus={safeStr(conversation.item?.status || 'active')}
              sellerId={safeStr(conversation.item?.sellerId)}
              sellerName={safeStr(conversation.item?.sellerUsername || conversation.item?.sellerName || 'Seller')}
              sellerAvatar={safeStr(conversation.item?.sellerAvatar)}
              buyerId={conversation.buyerId}
              buyerName={conversation.buyerName}
              buyerAvatar={conversation.buyerAvatar}
              startWithOffer={Boolean(conversation.startWithOffer)}
              currentUser={currentUserProfile as UserProfile}
              isDarkMode={isDarkMode}
              addToast={(message, type) => { addToast?.(message, type); }}
              onListingStatusChange={applyConversationListingStatus}
              onClose={closeConversationSmart}
            />
          </div>
        </div>, portalTarget!,
      )}

      {bazaarPortalReady && sellerMessagesOpen.open && createPortal(
        <div data-roddo-bazaar-portal="true" data-roddo-theme={isDarkMode ? 'dark' : 'light'} className={`${isDarkMode ? 'dark ' : ''}roddo-safe-overlay fixed inset-0 z-[99999] grid place-items-center overflow-y-auto bg-black/60 p-3 backdrop-blur-sm`} style={bazaarVars} onClick={() => setSellerMessagesOpen({ open: false, docKey: '', item: null })}>
          <div className={modalClass} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3"><div><h2 className="font-black">Listing messages</h2><div className="text-xs text-[var(--bazaar-text-soft)]">{safeStr(sellerMessagesOpen.item?.title)}</div></div><button type="button" onClick={() => setSellerMessagesOpen({ open: false, docKey: '', item: null })} className="h-10 w-10 rounded-full border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] text-[var(--bazaar-text)]">✕</button></div>
            <div className="mt-3 space-y-2">
              {sellerThreadsLoading ? <div className="text-sm font-semibold text-[var(--bazaar-text-soft)]">Loading messages…</div> : sellerThreads.length === 0 ? <div className="rounded-[16px] border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] p-4 text-sm font-semibold text-[var(--bazaar-text-soft)]">No Bazaar conversations yet.</div> : sellerThreads.map((thread) => (
                <button key={thread.id} type="button" onClick={() => openSellerConversation(thread)} className="w-full rounded-[14px] border border-[var(--bazaar-field-border)] bg-[var(--bazaar-field-bg)] p-3 text-left transition hover:-translate-y-0.5 hover:brightness-[1.03]">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)]">{thread.buyerAvatar ? <img src={thread.buyerAvatar} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-xs font-black">{safeStr(thread.buyerName).slice(0, 1).toUpperCase() || 'B'}</div>}</div>
                    <div className="min-w-0 flex-1"><div className="flex items-center gap-2"><div className="truncate text-sm font-black">{thread.buyerName || 'Buyer'}</div>{sellerInboxThreads.find((row) => row.id === thread.id)?.unread ? <span className="rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-black text-white">NEW</span> : null}</div><div className="mt-1 truncate text-xs font-semibold text-[var(--bazaar-text-soft)]">{thread.lastMessageText || 'Open conversation'}</div></div>
                    <span className="text-lg text-[var(--bazaar-text-soft)]">›</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>, portalTarget!,
      )}

      {bazaarPortalReady && report.open && createPortal(
        <div data-roddo-bazaar-portal="true" data-roddo-theme={isDarkMode ? 'dark' : 'light'} className={`${isDarkMode ? 'dark ' : ''}roddo-safe-overlay fixed inset-0 z-[99999] grid place-items-center overflow-y-auto bg-black/60 p-3 backdrop-blur-sm`} style={bazaarVars} onClick={() => setReport({ open: false, docKey: '', item: null })}>
          <div className={modalClass} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-black">Report listing</h2><button type="button" onClick={() => setReport({ open: false, docKey: '', item: null })} className="h-10 w-10 rounded-full border border-[var(--bazaar-border)] bg-[var(--bazaar-surface-bg)] text-[var(--bazaar-text)]">✕</button></div>
            <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} className={inputClass}><option value="prohibited_item">Prohibited or regulated item</option><option value="fraud">Fraud / scam</option><option value="counterfeit">Counterfeit</option><option value="unsafe">Unsafe item</option><option value="spam">Spam</option><option value="other">Other</option></select>
            <textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} maxLength={1200} rows={4} placeholder="Optional details" className={inputClass} />
            <button type="button" disabled={reporting} onClick={submitReport} className="mt-3 w-full rounded-[12px] bg-rose-600 px-4 py-3 text-sm font-black text-white disabled:opacity-50">{reporting ? 'Sending…' : 'Send report'}</button>
          </div>
        </div>, portalTarget!,
      )}

      <ConfirmationModal
        isOpen={soldConfirm.open}
        onClose={() => !markingSold && setSoldConfirm({ open: false, docKey: '', item: null })}
        onConfirm={markListingSold}
        title="Mark listing sold?"
        message={`Mark "${safeStr(soldConfirm.item?.title || soldConfirm.item?.name || 'this listing')}" as sold? It will be removed from active Bazaar listings and retained in seller history.`}
        confirmText={markingSold ? 'Marking sold…' : 'Mark sold'}
        cancelText="Keep active"
      />

      <ConfirmationModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, docKey: '', item: null })}
        onConfirm={deleteListing}
        title="Delete listing?"
        message={`Permanently delete "${safeStr(deleteConfirm.item?.title || deleteConfirm.item?.name || 'this listing')}"? This cannot be undone. Listings with transaction history cannot be hard-deleted.`}
        confirmText={deletingListing ? 'Deleting…' : 'Delete listing'}
        cancelText="Keep listing"
      />

      {bazaarPortalReady && lightboxUrl && createPortal(
        <div data-roddo-bazaar-portal="true" className="fixed inset-0 z-[100000] grid place-items-center bg-black/90 p-4" onClick={() => setLightboxUrl('')}><img src={lightboxUrl} alt="Listing full size" className="max-h-[90vh] max-w-[94vw] rounded-2xl object-contain" /></div>, portalTarget!,
      )}
    </div>
  );
}
